import { api } from '../_client';

/**
 * Flight-shaped reads that happen to live under `/api/files`.
 *
 * **`browseFiles` is gone from this client (STD-029 B-4, D-5).** The endpoint
 * itself is permanent — `GET /api/files/browse` is MissionPlanner's folder
 * picker (D-4b) and is versioned external surface — but the dashboard stopped
 * consuming it: its flat, recursive, path-keyed projection could never address a
 * folder another organization shared, because a path is a coordinate inside one
 * organization's own tree. The browser's one navigation contract is
 * `filesChildrenApi.listChildren` (`./children.ts`).
 *
 * Nothing below is a listing. They are flight reads the analytics handoff and the
 * dashboard cards need, and they stay exactly as they were.
 */
export const filesBrowseApi = {
  // Get flights for plotting (optimized endpoint)
  async getFlightsForPlotting(
    droneSerial: string,
    startDate: string,
    endDate: string
  ): Promise<{
    success: boolean;
    flights: Array<{
      flight_id: string;
      /**
       * Where the bytes are. Opaque since #39 Phase C — a minted uuid for
       * anything uploaded after it, a path for legacy rows. Never parse it; it
       * is only the *fallback* address for the analytics handoff.
       */
      s3_key: string;
      /** `fleet.file_objects` row id — the address the handoff prefers (#1231). */
      object_id: string | null;
      /** The log's original filename. Null on legacy rows (#39 Phase B2). */
      log_basename: string | null;
      flight_date: string;
      flight_type: string;
      serial_number: string;
    }>;
    total_flights: number;
    date_range: { start_date: string; end_date: string };
    message?: string;
  }> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    });
    const response = await api.get(
      `/api/files/flights-for-plotting/${encodeURIComponent(droneSerial)}?${params.toString()}`
    );
    return response.data;
  },

  // Get latest flights across all drones (org-scoped via RLS)
  async getLatestFlights(
    limit: number = 5,
    offset: number = 0
  ): Promise<{
    success: boolean;
    flights: Array<{
      id: string;
      drone_serial: string;
      flight_date: string;
      flight_hours: number | null;
      /** Where the bytes are. Opaque since #39 Phase C — never parse it. */
      s3_key: string | null;
      /** The log's original filename. Null on legacy rows (#39 Phase B2). */
      log_basename: string | null;
    }>;
    limit: number;
    offset: number;
  }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    const response = await api.get(`/api/files/latest-flights?${params.toString()}`);
    return response.data;
  },

  // Get flight details for a specific date
  async getFlightDetails(
    droneSerial: string,
    flightDate: string
  ): Promise<{
    success: boolean;
    flights: Array<{
      flight_id: string;
      flight_type: string;
      pilot_id: string | null;
      pilot_name: string | null;
      payload_id: number | null;
      payload_name: string | null;
      customer_id: string | null;
      customer_name: string | null;
      project_id: string | null;
      project_name: string | null;
      airspace_observer_id: string | null;
      airspace_observer_name: string | null;
      payload_operator_id: string | null;
      payload_operator_name: string | null;
      engine_hours: number | null;
      flight_hours: number | null;
      download_url: string;
      /** Where the bytes are. Opaque since #39 Phase C — never parse it. */
      s3_key: string;
      /** `fleet.file_objects` row id — the address Data Analysis Pro prefers (#1294). */
      object_id: string | null;
      /** The log's original filename. Null on legacy rows (#39 Phase B2). */
      log_basename: string | null;
      weather: {
        temperature_c: number | null;
        humidity_percent: number | null;
        wind_speed_ms: number | null;
        wind_direction_deg: number | null;
        precipitation_mm: number | null;
        is_raining: boolean | null;
        fetched_at: string | null;
      } | null;
      metadata: Record<string, unknown> | null;
    }>;
    message?: string;
  }> {
    const params = new URLSearchParams({
      flight_date: flightDate,
    });
    const response = await api.get(
      `/api/files/flight-details/${encodeURIComponent(droneSerial)}?${params.toString()}`
    );
    return response.data;
  },
};
